/**
 * Product Personalizer DUAL v2 — Shopify Block Script
 *
 * For products with two-sided personalization (front/back).
 * Each side: customer chooses Photo or Text.
 * Text = free textarea with custom font, auto-shrink on canvas.
 * Photo = upload image + zoom/pan editor.
 * Single canvas preview (both sides in one image).
 *
 * Background image changes based on mode combination + color.
 * 4 image keys per color: imgPhotoText, imgTextPhoto, imgPhotoPhoto, imgTextText.
 *
 * Integrates into the product form by injecting hidden inputs.
 * Triple interceptor: form submit + fetch + XHR (for ReelAsIt).
 */

(function() {
  'use strict';

  // JPEG no tiene transparencia: lo transparente se vuelve NEGRO al exportar. Antes de
  // exportar se calca el canvas sobre blanco (si ya era opaco, no cambia nada).
  // Bug real 05/08/2026: los fondos con transparencia del Tag de Mascota se guardaban oscuros.
  function __pzSobreBlanco(cv) {
    var c = document.createElement('canvas');
    c.width = cv.width; c.height = cv.height;
    var x = c.getContext('2d');
    x.fillStyle = '#ffffff';
    x.fillRect(0, 0, c.width, c.height);
    x.drawImage(cv, 0, 0);
    return c;
  }


  // ============================================================
  // CLASS
  // ============================================================
  class ProductPersonalizerDual {
    constructor(el) {
      this.el = el;
      this.config = null;
      this.productForm = null;
      this._previewUrl = null;

      // Single canvas
      this.canvas = el.querySelector('[data-pzd-canvas]');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;

      // Current background image
      this._bgImg = null;
      this._bgImages = {}; // cache: "colorId_imgKey" -> Image

      // Per-side state
      this.sideState = {
        front: { mode: 'photo', text: '', photoFile: null, photoUrl: null, photoUploadUrl: null, photoTransform: { x: 0, y: 0, scale: 1, rotation: 0 }, uploading: false },
        back:  { mode: 'text',  text: '', photoFile: null, photoUrl: null, photoUploadUrl: null, photoTransform: { x: 0, y: 0, scale: 1, rotation: 0 }, uploading: false }
      };

      // Shared state
      this.state = {
        activeSide: 'front',
        color: null,
        font: null,
        variantId: null
      };

      // Font loading tracker
      this._fontsLoaded = {};

      // V2: No canvas drag/pinch — manual controls only

      this._init();
    }

    // ============================================================
    // INIT
    // ============================================================
    _init() {
      try {
        this.config = JSON.parse(this.el.dataset.pzdConfig);
      } catch(e) {
        console.error('[PZD] Invalid config:', e);
        return;
      }

      if (this.config.type !== 'dual') return;

      var sidesConfig = this.config.sides || {};

      // Set default modes from config
      var frontCfg = sidesConfig.front || {};
      var backCfg = sidesConfig.back || {};
      this.sideState.front.mode = frontCfg.defaultMode || 'photo';
      this.sideState.back.mode = backCfg.defaultMode || 'text';

      // Variant ID
      this.state.variantId = this.el.dataset.pzdVariantId || null;

      // Variantes de Shopify (expuestas por el liquid) — stock real por color
      try {
        this.shopifyVariants = JSON.parse(this.el.dataset.pzdVariants || '[]');
      } catch (e) {
        this.shopifyVariants = [];
      }
      try {
        this.shopifyOptions = JSON.parse(this.el.dataset.pzdOptions || '[]');
      } catch (e) {
        this.shopifyOptions = [];
      }
      // Disponibilidad global del producto (para productos simples sin variantes)
      this.productAvailable = this.el.dataset.pzdProductAvailable === 'true';
      this.variantMap = {};
      this._buildVariantMap();

      // First available color (preferir uno con stock real)
      var self0 = this;
      var colors = this.config.colors || [];
      var firstColor = colors.find(function(c) {
        return c.available !== false && self0._isVariantAvailable(c.name);
      });
      if (!firstColor) firstColor = colors.find(function(c) { return c.available !== false; });
      this.state.color = firstColor ? firstColor.id : null;

      // Font
      var fonts = this.config.fonts || [];
      this.state.font = fonts.length > 0 ? fonts[0].family : null;

      // Load custom fonts
      this._loadFonts();

      // Apply per-product styles
      this._applyStyles();

      // Show/hide sections
      this._showSections();

      // Render shared sections
      if (this._sectionVisible('colors')) this._renderColors();
      if (this._sectionVisible('fonts'))  this._renderFontSelector();

      // Update labels from config
      this._updateLabels();

      // Set initial color badge (must be after _updateLabels which rebuilds the label DOM)
      this._updateBadge();

      // Setup modes from config defaults
      this._initModes();

      // Setup accordion panels (v2)
      this._setupAccordion();

      // Find product form and integrate
      this.productForm = this._findProductForm();
      if (this.productForm) {
        this._injectHiddenInputs();
        this._interceptFormSubmit();
      }

      // Stock real de Shopify (solo si el sync está activo)
      this._hideThemeVariantSelectors();
      this._updateVariantId();
      this._checkGlobalStock();

      // ReelAsIt interceptor
      this._interceptReelAsItButton();

      // AJAX interceptors
      this._interceptFetchAddToCart();
      this._interceptXHRAddToCart();

      // Load background image
      this._loadBackgroundImage();

      // Bind events
      this._bindEvents();
      this._updateStatus();

      // Mark as loaded
      this.el.classList.add('is-loaded');
    }

    // ============================================================
    // SHOPIFY VARIANTS — stock real por color
    // ============================================================
    _buildVariantMap() {
      var self = this;
      var colorOpt = (this.config.shopifyColorOption || 'Color').toLowerCase();

      (this.shopifyVariants || []).forEach(function(v) {
        var color = null;
        if (v.option1 !== undefined) {
          // Formato posicional: option1/2/3 según el orden de product.options
          var opts = self.shopifyOptions || [];
          [v.option1, v.option2, v.option3].forEach(function(val, idx) {
            if (!val || !opts[idx]) return;
            if ((opts[idx].name || '').toLowerCase() === colorOpt) color = val;
          });
        } else if (Array.isArray(v.options)) {
          v.options.forEach(function(o) {
            if ((o.name || '').toLowerCase() === colorOpt) color = o.value;
          });
        }
        if (color) {
          self.variantMap[color] = { id: v.id, available: v.available !== false };
        }
      });
    }

    // ¿Producto simple (sin variantes reales, solo "Default Title")?
    _isSimpleProduct() {
      var opts = this.shopifyOptions || [];
      if (opts.length === 0) return true;
      if (opts.length === 1 && (opts[0].name || '').toLowerCase() === 'title') return true;
      return false;
    }

    _isVariantAvailable(colorName) {
      // 1) Sync desactivado en el admin → siempre disponible (vende sin control de stock).
      if (this.config.enableStockSync !== true) return true;
      // 2) Producto simple (sin variantes) → usar el stock GLOBAL del producto.
      if (this._isSimpleProduct()) return this.productAvailable;
      // 3) Sync activo pero no se mapeó ninguna variante (nombres que no coinciden) → no bloquear la venta.
      //    El aviso de "mapeo incompleto" sale en el admin, no acá.
      if (Object.keys(this.variantMap).length === 0) return true;
      if (!colorName) return false;
      var v = this.variantMap[colorName];
      return v ? v.available : false;
    }

    _getVariantId(colorName) {
      if (!colorName) return null;
      var v = this.variantMap[colorName];
      return v ? v.id : null;
    }

    _getActiveColor() {
      var self = this;
      return (this.config.colors || []).find(function(c) { return c.id === self.state.color; }) || null;
    }

    _updateVariantId() {
      if (this.config.enableStockSync !== true) return;
      var color = this._getActiveColor();
      if (!color) return;
      var id = this._getVariantId(color.name);
      if (!id) return;
      this.state.variantId = id;
      // Actualizar también el input name="id" del form de Shopify
      if (this.productForm) {
        var idInput = this.productForm.querySelector('input[name="id"], select[name="id"]');
        if (idInput) {
          idInput.value = id;
          // Avisar al theme por si escucha el cambio (algunos actualizan el precio con esto)
          try { idInput.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
        }
      }
      this._injectHiddenInputs();
    }

    // Si es producto simple, el sync está activo y el stock global es 0 → aviso persistente.
    _checkGlobalStock() {
      if (this.config.enableStockSync === true && this._isSimpleProduct() && !this.productAvailable) {
        var box = this.el.querySelector('[data-pzd-msg="error"]');
        if (box) {
          box.textContent = 'Producto agotado temporalmente.';
          box.classList.add('is-visible');
        }
      }
    }

    _hideThemeVariantSelectors() {
      if (this.config.enableStockSync !== true) return;

      // Selectores de variantes del theme: suelen estar FUERA del form del producto.
      var optionInputs = document.querySelectorAll(
        'input[name^="options"], input[name^="Options"], ' +
        'select[name^="options"], select[name^="Options"]'
      );
      optionInputs.forEach(function(input) {
        var wrapper = input.closest(
          'fieldset, variant-radios, variant-selects, ' +
          '.product-form__input, .product-form__option, .product-options, ' +
          '.variant-input-wrap, .swatch, [data-product-option]'
        );
        if (wrapper) wrapper.style.display = 'none';
      });

      document.querySelectorAll(
        'variant-radios, variant-selects, ' +
        '.product-form__variants, .product-form__option-selectors, ' +
        '.product-info__variant-picker, [data-variant-selector], ' +
        '.vvp'  // theme Toto V.7 (Mistikal) - variant visual picker propio
      ).forEach(function(el) {
        el.style.display = 'none';
      });
    }

    // ============================================================
    // LOAD CUSTOM FONTS (from config fonts[].src)
    // ============================================================
    _loadFonts() {
      var self = this;
      var fonts = this.config.fonts || [];

      fonts.forEach(function(f) {
        if (!f.src || self._fontsLoaded[f.family]) return;

        try {
          var fontFace = new FontFace(f.family, 'url(' + f.src + ')');
          fontFace.load().then(function(loaded) {
            document.fonts.add(loaded);
            self._fontsLoaded[f.family] = true;
            // Update textarea font if this is the current font
            if (self.state.font === f.family) {
              self._updateTextareaFonts();
            }
            // Redraw canvas
            self._drawCanvas();
          }).catch(function(err) {
            console.warn('[PZD] Font load failed:', f.family, err);
          });
        } catch(err) {
          console.warn('[PZD] FontFace error:', f.family, err);
        }
      });
    }

    // ============================================================
    // INIT MODES — set initial mode per side from config
    // ============================================================
    _initModes() {
      var sideKeys = ['front', 'back'];
      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        this._applyMode(key, this.sideState[key].mode);
      }
    }

    _applyMode(sideKey, mode) {
      this.sideState[sideKey].mode = mode;

      // Update mode toggle buttons
      var toggle = this.el.querySelector('[data-pzd-mode-toggle="' + sideKey + '"]');
      if (toggle) {
        toggle.querySelectorAll('[data-pzd-mode]').forEach(function(btn) {
          btn.classList.toggle('is-active', btn.dataset.pzdMode === mode);
        });
      }

      // Show/hide text/photo content + font picker
      var sideEl = this.el.querySelector('[data-pzd-panel-body="' + sideKey + '"]');
      var textContent = this.el.querySelector('[data-pzd-text-content="' + sideKey + '"]');
      var photoContent = this.el.querySelector('[data-pzd-photo-content="' + sideKey + '"]');

      if (textContent) textContent.style.display = mode === 'text' ? '' : 'none';
      if (photoContent) photoContent.style.display = mode === 'photo' ? '' : 'none';

      // Show/hide font picker inside this side (only in text mode + 2+ fonts)
      if (sideEl) {
        var fontSection = sideEl.querySelector('[data-pzd-section="fonts"]');
        if (fontSection) {
          var fontsEnabled = (this.config.fonts || []).length > 1;
          fontSection.style.display = (mode === 'text' && fontsEnabled) ? '' : 'none';
        }
      }

      // Update textarea font
      if (mode === 'text') {
        this._updateTextareaFont(sideKey);
        this._updateCharCount(sideKey);
      }

      // If switching to photo mode and already has a photo, hide upload area
      if (mode === 'photo') {
        var ss = this.sideState[sideKey];
        if (ss.photoUrl) {
          var upload = this.el.querySelector('[data-pzd-photo-upload="' + sideKey + '"]');
          if (upload) upload.style.display = 'none';
        }
      }

      // Update canvas interaction state (photo controls, cursor, hint)
      this._updateCanvasPhotoControls();
    }

    // ============================================================
    // TABS — switch between front/back
    // ============================================================
    // V2: Accordion panels instead of tabs
    _setupAccordion() {
      var self = this;
      var btns = this.el.querySelectorAll('[data-pzd-panel-btn]');
      btns.forEach(function(btn) {
        btn.addEventListener('click', function() {
          self._switchPanel(btn.dataset.pzdPanelBtn);
        });
      });
    }

    _switchPanel(sideKey) {
      this.state.activeSide = sideKey;

      // Toggle tab buttons: active on clicked, inactive on others
      this.el.querySelectorAll('[data-pzd-panel-btn]').forEach(function(btn) {
        btn.classList.toggle('is-active', btn.dataset.pzdPanelBtn === sideKey);
      });

      // Toggle panels: open clicked, close others
      this.el.querySelectorAll('[data-pzd-panel]').forEach(function(p) {
        p.classList.toggle('is-open', p.dataset.pzdPanel === sideKey);
      });

      // Redraw canvas to reflect active side
      this._drawCanvas();
    }

    // V2: Alias for compatibility — some code calls _switchSide
    _switchSide(sideKey) {
      this._switchPanel(sideKey);
    }

    // ============================================================
    // FIND PRODUCT FORM
    // ============================================================
    _findProductForm() {
      var formId = this.el.dataset.pzdFormId;
      if (formId) {
        var form = document.getElementById(formId);
        if (form) return form;
      }

      var selectors = [
        'form[action="/cart/add"]',
        'form[action^="/cart/add"]',
        'product-form form',
        'form.feature-product-form',
        'form[data-type="add-to-cart-form"]'
      ];

      for (var i = 0; i < selectors.length; i++) {
        var form = document.querySelector(selectors[i]);
        if (form) return form;
      }

      return null;
    }

    // ============================================================
    // HIDDEN INPUTS — inject properties into form
    // ============================================================
    _injectHiddenInputs() {
      var container = this.el.querySelector('[data-pzd-hidden-inputs]');
      if (!container) return;

      container.innerHTML = '';

      var props = this._buildProperties();
      var formId = this.productForm ? this.productForm.id : null;

      for (var key in props) {
        if (!props.hasOwnProperty(key)) continue;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + key + ']';
        input.value = props[key];
        input.setAttribute('data-pzd-property', '');
        if (formId) input.setAttribute('form', formId);
        container.appendChild(input);
      }
    }

    // ============================================================
    // BUILD PROPERTIES
    // ============================================================
    _buildProperties() {
      var props = {};
      var sidesConfig = this.config.sides || {};
      var cl = this.config.cartLabels || {};

      // Color (shared)
      if (this._sectionVisible('colors')) {
        var colors = this.config.colors || [];
        var self = this;
        var colorCfg = colors.find(function(c) { return c.id === self.state.color; });
        var colorKey = cl.color || this.config.colorLabel || 'Color';
        props[colorKey] = colorCfg ? colorCfg.name : this.state.color;
      }

      // Font (shared)
      if (this._sectionVisible('fonts')) {
        var fonts = this.config.fonts || [];
        var self = this;
        var f = fonts.find(function(f) { return f.family === self.state.font; });
        var fontKey = cl.font || this.config.fontLabel || 'Tipografía';
        props[fontKey] = f ? (f.label || f.family) : this.state.font;
      }

      // Per side
      var sideKeys = ['front', 'back'];
      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        var sideCfg = sidesConfig[key] || {};
        var sideLabel = sideCfg.label || (key === 'front' ? 'Adelante' : 'Atrás');
        var ss = this.sideState[key];

        if (ss.mode === 'text') {
          var textKey = cl[key + 'Text'] || (sideLabel + ' (Texto)');
          var fontCfg = this._getFontConfig();
          props[textKey] = fontCfg.uppercase !== false ? ss.text.toUpperCase() : ss.text;
        } else if (ss.mode === 'photo') {
          var photoKey = cl[key + 'Photo'] || sideLabel;
          props[photoKey] = 'Foto personalizada';
          if (ss.photoUploadUrl) {
            props['_' + photoKey + ' (Foto URL)'] = ss.photoUploadUrl;
          } else {
            props['_' + photoKey + ' (Foto URL)'] = 'Pendiente';
          }
        }
      }

      // Preview URL
      if (this._previewUrl) {
        props['_Vista Previa'] = this._previewUrl;
      }

      // Código único para el botón "Ver mi diseño" del carrito (derivado del preview).
      // Se pega a la ÚLTIMA propiedad visible → el botón queda al FINAL de la lista.
      if (this._previewUrl) {
        var _cref = String(this._previewUrl).replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase();
        var _cvis = Object.keys(props).filter(function (k) { return k.charAt(0) !== '_'; });
        if (_cref && _cvis.length) {
          props[_cvis[_cvis.length - 1]] += ' (#' + _cref + ')';
          props['_ref'] = _cref;
        }
      }

      return props;
    }

    // ============================================================
    // INTERCEPT FORM SUBMIT
    // ============================================================
    _interceptFormSubmit() {
      if (!this.productForm) return;
      var self = this;

      this.productForm.addEventListener('submit', function(e) {
        self._injectHiddenInputs();

        if (!self._validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
      }, true);
    }

    // ============================================================
    // INTERCEPT FETCH — for AJAX add-to-cart
    // ============================================================
    _interceptFetchAddToCart() {
      var self = this;
      var originalFetch = window.fetch;

      window.fetch = function(url, options) {
        var fetchCtx = this;
        var urlStr = typeof url === 'string' ? url : (url instanceof Request ? url.url : '');
        if (!urlStr.includes('/cart/add')) {
          return originalFetch.apply(fetchCtx, arguments);
        }

        if (!options || !options.body) {
          return originalFetch.apply(fetchCtx, arguments);
        }

        // Validate before allowing add-to-cart
        self._injectHiddenInputs();
        if (!self._validate()) {
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return Promise.reject(new Error('Personalización incompleta'));
        }

        var pzProps = self._buildProperties();

        if (options.body instanceof FormData) {
          for (var key in pzProps) {
            options.body.set('properties[' + key + ']', pzProps[key]);
          }
        } else if (typeof options.body === 'string') {
          var injected = false;
          try {
            var data = JSON.parse(options.body);
            if (typeof data === 'object' && data !== null) {
              data.properties = Object.assign({}, data.properties || {}, pzProps);
              options = Object.assign({}, options, { body: JSON.stringify(data) });
              injected = true;
            }
          } catch(err) {}

          if (!injected && options.body.indexOf('=') !== -1) {
            var params = new URLSearchParams(options.body);
            for (var key in pzProps) {
              params.set('properties[' + key + ']', pzProps[key]);
            }
            options = Object.assign({}, options, { body: params.toString() });
          }
        }

        return originalFetch.apply(fetchCtx, [url, options]);
      };
    }

    // ============================================================
    // INTERCEPT XMLHttpRequest — for ReelAsIt and other apps
    // ============================================================
    _interceptXHRAddToCart() {
      var self = this;
      var originalOpen = XMLHttpRequest.prototype.open;
      var originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function(method, url) {
        this._pzdUrl = typeof url === 'string' ? url : '';
        this._pzdMethod = method;
        return originalOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function(body) {
        if (!this._pzdUrl || !this._pzdUrl.includes('/cart/add')) {
          return originalSend.apply(this, arguments);
        }

        if (!body) {
          return originalSend.apply(this, arguments);
        }

        // Validate before allowing add-to-cart
        self._injectHiddenInputs();
        if (!self._validate()) {
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.abort();
          return;
        }

        var pzProps = self._buildProperties();

        if (body instanceof FormData) {
          for (var key in pzProps) {
            body.set('properties[' + key + ']', pzProps[key]);
          }
          return originalSend.apply(this, [body]);
        }

        if (typeof body === 'string') {
          var injected = false;
          try {
            var data = JSON.parse(body);
            if (typeof data === 'object' && data !== null) {
              data.properties = Object.assign({}, data.properties || {}, pzProps);
              body = JSON.stringify(data);
              injected = true;
            }
          } catch(err) {}

          if (!injected && body.indexOf('=') !== -1) {
            var params = new URLSearchParams(body);
            for (var key in pzProps) {
              params.set('properties[' + key + ']', pzProps[key]);
            }
            body = params.toString();
          }
        }

        return originalSend.apply(this, [body]);
      };
    }

    // ============================================================
    // INTERCEPT REELAIST BUTTON
    // ============================================================
    _interceptReelAsItButton() {
      var self = this;

      document.addEventListener('click', function(e) {
        var rsiBtn = e.target.closest('._rsi-buy-now-button, [class*="_rsi-buy-now"]');
        if (!rsiBtn) return;

        var insideModal = e.target.closest('#_rsi-cod-form-modal');
        if (insideModal) return;

        // Si el botón NO está en la misma sección de producto que el personalizador,
        // dejarlo pasar (ej: botón de ReelAsIt en el slider/drawer del carrito)
        var productSection = self.el.closest('[data-section-id], .shopify-section, section');
        if (productSection && !productSection.contains(rsiBtn)) return;

        // Re-inject before validation
        self._injectHiddenInputs();

        if (!self._validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true);
    }

    // ============================================================
    // SECTION VISIBILITY
    // ============================================================
    _showSections() {
      var colors = this.config.colors || [];

      // Colors section (single)
      var colorSection = this.el.querySelector('[data-pzd-section="colors"]');
      if (colorSection && colors.length > 0) {
        colorSection.style.display = '';
      }

      // Font sections are managed by _applyMode (shown only in text mode when 2+ fonts)
    }

    _sectionVisible(name) {
      // For fonts, check config directly since display is managed by _applyMode
      if (name === 'fonts') {
        return (this.config.fonts || []).length > 1;
      }
      var section = this.el.querySelector('[data-pzd-section="' + name + '"]');
      return section && section.style.display !== 'none';
    }

    // ============================================================
    // UPDATE LABELS FROM CONFIG
    // ============================================================
    _updateLabels() {
      var self = this;
      var sidesConfig = this.config.sides || {};

      // Color label
      if (this.config.colorLabel) {
        var lbl = this.el.querySelector('[data-pzd-color-label]');
        if (lbl) {
          var badge = lbl.querySelector('[data-pzd-color-name]');
          lbl.textContent = this.config.colorLabel + ' ';
          if (badge) lbl.appendChild(badge);
        }
      }

      // Tabs section label (e.g. "Selecciona que llevara tu placa")
      if (this.config.tabsLabel !== undefined) {
        var tabsLabel = this.el.querySelector('[data-pzd-tabs-label]');
        if (tabsLabel) tabsLabel.textContent = this.config.tabsLabel || '';
        // Hide label element entirely if empty
        if (tabsLabel && !this.config.tabsLabel) tabsLabel.style.display = 'none';
      }

      // Font label
      if (this.config.fontLabel !== undefined) {
        var fontLabels = this.el.querySelectorAll('[data-pzd-font-label]');
        fontLabels.forEach(function(lbl) {
          lbl.textContent = self.config.fontLabel || 'Tipografía';
        });
      }

      // Tab labels
      var sideKeys = ['front', 'back'];
      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        var sideCfg = sidesConfig[key] || {};
        if (sideCfg.label) {
          var tabLabel = this.el.querySelector('[data-pzd-tab-label="' + key + '"]');
          if (tabLabel) tabLabel.textContent = sideCfg.label;
        }
      }

      // Preview label
      if (this.config.previewLabel) {
        var lbl = this.el.querySelector('[data-pzd-preview-label]');
        if (lbl) lbl.textContent = this.config.previewLabel;
      }

      // Preview hint
      if (this.config.previewHint !== undefined) {
        var hint = this.el.querySelector('[data-pzd-preview-hint]');
        if (hint) hint.textContent = this.config.previewHint ? '(' + this.config.previewHint + ')' : '';
      }

      // Preview button label
      if (this.config.previewButtonLabel !== undefined) {
        var btnLabels = this.el.querySelectorAll('[data-pzd-preview-btn-label]');
        btnLabels.forEach(function(lbl) {
          lbl.textContent = self.config.previewButtonLabel || 'Ver vista previa';
        });
      }

      // Preview button font size
      if (this.config.previewButtonFontSize) {
        var btns = this.el.querySelectorAll('[data-pzd-open-preview]');
        btns.forEach(function(btn) {
          btn.style.setProperty('--pzd-preview-btn-fs', self.config.previewButtonFontSize + 'px');
        });
      }
    }


    // ============================================================
    // PER-PRODUCT STYLES
    // ============================================================
    _applyStyles() {
      var s = this.config.styles || {};

      var map = {
        'accent':        s.accentColor,
        'accent-hover':  s.accentHover,
        'card-bg':       s.cardBg,
        'border':        s.borderColor,
        'input-bg':      s.inputBg,
        'text':          s.textColor,
        'text-muted':    s.mutedColor,
        'label-weight':  s.labelBold === false ? '500' : '700',
        'label-size':    s.labelSize,
        'tab-size':      s.tabSize,
        'input-size':    s.inputSize,
        'radius':        s.radius,
        'btn-bg':        s.btnBg,
        'btn-text':      s.btnText,
        'btn-radius':    s.btnRadius
      };

      for (var key in map) {
        if (!map.hasOwnProperty(key)) continue;
        if (map[key] != null && map[key] !== '') {
          this.el.style.setProperty('--pzd-' + key, map[key]);
        }
      }
    }

    // ============================================================
    // GET IMAGE KEY — based on mode combination
    // ============================================================
    _getImageKey() {
      var fm = this.sideState.front.mode;
      var bm = this.sideState.back.mode;
      return 'img' +
        (fm === 'photo' ? 'Photo' : 'Text') +
        (bm === 'photo' ? 'Photo' : 'Text');
    }

    _getImageUrl() {
      var colors = this.config.colors || [];
      var self = this;
      var color = colors.find(function(c) { return c.id === self.state.color; });
      if (!color) return '';
      var key = this._getImageKey();
      return color[key] || '';
    }

    // ============================================================
    // LOAD BACKGROUND IMAGE
    // ============================================================
    _loadBackgroundImage() {
      var url = this._getImageUrl();
      if (!url) {
        this._bgImg = null;
        this._drawCanvas();
        var loading = this.el.querySelector('[data-pzd-loading]');
        if (loading) loading.classList.add('is-hidden');
        return;
      }

      var cacheKey = this.state.color + '_' + this._getImageKey();

      // Check cache
      if (this._bgImages[cacheKey]) {
        this._bgImg = this._bgImages[cacheKey];
        this._drawCanvas();
        var loading = this.el.querySelector('[data-pzd-loading]');
        if (loading) loading.classList.add('is-hidden');
        return;
      }

      var self = this;
      var loading = this.el.querySelector('[data-pzd-loading]');
      if (loading) loading.classList.remove('is-hidden');

      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        self._bgImages[cacheKey] = img;
        self._bgImg = img;
        self._drawCanvas();
        if (loading) loading.classList.add('is-hidden');
      };
      img.onerror = function() {
        console.warn('[PZD] Image failed:', url);
        self._bgImg = null;
        self._drawCanvas();
        if (loading) loading.classList.add('is-hidden');
      };
      img.src = url;
    }

    // ============================================================
    // DRAWING — single canvas with both sides
    // ============================================================
    _drawCanvas() {
      if (!this.canvas || !this.ctx) return;

      if (!this._bgImg) {
        // Placeholder
        this.canvas.width = 800;
        this.canvas.height = 400;
        this.ctx.fillStyle = '#e8e5e0';
        this.ctx.fillRect(0, 0, 800, 400);
        this.ctx.fillStyle = '#aaa';
        this.ctx.font = '14px sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.fillText('Sin imagen de fondo', 400, 200);
        return;
      }

      var w = this._bgImg.naturalWidth;
      var h = this._bgImg.naturalHeight;
      this.canvas.width = w;
      this.canvas.height = h;

      this.ctx.clearRect(0, 0, w, h);

      var sidesConfig = this.config.sides || {};
      var sideKeys = ['front', 'back'];
      var s, key, ss;

      // Phase 1: Draw customer photos FIRST (behind everything)
      for (s = 0; s < sideKeys.length; s++) {
        key = sideKeys[s];
        ss = this.sideState[key];
        if (ss.mode === 'photo' && ss.photoUrl && ss._photoImg && ss._photoImg.complete) {
          this._drawPhotoOnCanvas(key, w, h);
        }
      }

      // Phase 2: Product image ON TOP (PNG with transparency reveals photos underneath)
      this.ctx.drawImage(this._bgImg, 0, 0, w, h);

      // Phase 3: Text ON TOP of everything
      for (s = 0; s < sideKeys.length; s++) {
        key = sideKeys[s];
        ss = this.sideState[key];
        if (ss.mode === 'text' && ss.text.trim()) {
          this._drawTextOnCanvas(key, w, h);
        }
      }

      // Schedule preview upload
      this._schedulePreviewUpload();
    }

    // ============================================================
    // GET AREA — per-color override with fallback to sides config
    // ============================================================
    _getArea(sideKey, type) {
      // type = 'text' or 'photo'
      var defaults = type === 'text'
        ? { x: 0.10, y: 0.15, width: 0.80, height: 0.70 }
        : { x: 0.05, y: 0.05, width: 0.90, height: 0.90 };

      // Check per-color areas first
      var colors = this.config.colors || [];
      var self = this;
      var color = colors.find(function(c) { return c.id === self.state.color; });
      if (color && color.areas && color.areas[sideKey] && color.areas[sideKey][type]) {
        return color.areas[sideKey][type];
      }

      // Fallback to sides config
      var sidesConfig = this.config.sides || {};
      var sideCfg = sidesConfig[sideKey] || {};
      var typeCfg = sideCfg[type] || {};
      return typeCfg.area || defaults;
    }

    // ============================================================
    // DRAW TEXT — auto-shrink free text with word-wrap
    // ============================================================
    _drawTextOnCanvas(sideKey, w, h) {
      var sidesConfig = this.config.sides || {};
      var sideCfg = sidesConfig[sideKey] || {};
      var textCfg = sideCfg.text || {};
      var area = this._getArea(sideKey, 'text');
      var ss = this.sideState[sideKey];
      var fontCfg = this._getFontConfig();

      var family = this.state.font || 'monospace';
      var align = 'center';
      var textColor = textCfg.color || '#000000';
      var shadowColor = textCfg.shadowColor || 'rgba(255, 255, 255, 0.3)';
      var sx = textCfg.shadowOffsetX != null ? textCfg.shadowOffsetX : 1;
      var sy = textCfg.shadowOffsetY != null ? textCfg.shadowOffsetY : 1;
      var lineHeightRatio = fontCfg.lineHeight || 1.4;

      var areaX = w * area.x;
      var areaY = h * area.y;
      var areaW = w * area.width;
      var areaH = h * area.height;

      var maxPx = Math.round(w * (textCfg.maxFontSize || 0.07));
      var minPx = Math.round(w * (textCfg.minFontSize || 0.025));

      // Split text by newlines — uppercase depends on font config
      var textStr = fontCfg.uppercase !== false ? ss.text.toUpperCase() : ss.text;
      var rawLines = textStr.split('\n');

      // Apply per-font spacing BEFORE measuring (Chrome 99+, Safari 16+)
      // This ensures _findFittingFontSize and _wrapText account for spacing
      this.ctx.save();
      if (fontCfg.letterSpacing) {
        this.ctx.letterSpacing = fontCfg.letterSpacing + 'em';
      }
      if (fontCfg.wordSpacing) {
        this.ctx.wordSpacing = fontCfg.wordSpacing + 'em';
      }

      // Find fitting font size with word-wrap (spacing already applied to ctx)
      var fontSize = this._findFittingFontSize(rawLines, areaW, areaH, minPx, maxPx, family, lineHeightRatio);

      this.ctx.textAlign = align;
      this.ctx.textBaseline = 'middle';
      this.ctx.font = 'bold ' + fontSize + 'px "' + family + '", monospace';

      // Word-wrap all lines at the found font size
      var wrappedLines = [];
      for (var i = 0; i < rawLines.length; i++) {
        var line = rawLines[i];
        if (!line.trim()) { wrappedLines.push(''); continue; }
        var wrapped = this._wrapText(line, areaW);
        for (var j = 0; j < wrapped.length; j++) {
          wrappedLines.push(wrapped[j]);
        }
      }

      // X anchor
      var x;
      if (align === 'left') x = areaX;
      else if (align === 'center') x = areaX + areaW / 2;
      else x = areaX + areaW;

      // Vertical centering
      var totalH = wrappedLines.length * fontSize * lineHeightRatio;
      var startY = areaY + (areaH - totalH) / 2 + fontSize * lineHeightRatio / 2;
      // Offset vertical por fuente (en em, multiplicado por fontSize para convertir a px)
      startY += (fontCfg.yOffset || 0) * fontSize;

      for (var i = 0; i < wrappedLines.length; i++) {
        var text = wrappedLines[i];
        if (!text) continue;
        var y = startY + i * fontSize * lineHeightRatio;

        // Shadow
        this.ctx.fillStyle = shadowColor;
        this.ctx.fillText(text, x + sx, y + sy);
        // Main text
        this.ctx.fillStyle = textColor;
        this.ctx.fillText(text, x, y);
      }

      this.ctx.restore();
    }

    _wrapText(text, maxWidth) {
      var words = text.split(' ');
      var lines = [];
      var current = '';

      for (var i = 0; i < words.length; i++) {
        var word = words[i];

        // If a single word is wider than maxWidth, break it character by character
        if (this.ctx.measureText(word).width > maxWidth) {
          if (current) { lines.push(current); current = ''; }
          var chunk = '';
          for (var c = 0; c < word.length; c++) {
            var testChunk = chunk + word[c];
            if (this.ctx.measureText(testChunk).width > maxWidth && chunk) {
              lines.push(chunk);
              chunk = word[c];
            } else {
              chunk = testChunk;
            }
          }
          current = chunk;
          continue;
        }

        var test = current ? current + ' ' + word : word;
        if (this.ctx.measureText(test).width > maxWidth && current) {
          lines.push(current);
          current = word;
        } else {
          current = test;
        }
      }
      if (current) lines.push(current);
      return lines;
    }

    _findFittingFontSize(rawLines, areaW, areaH, minPx, maxPx, family, lineHeightRatio) {
      if (rawLines.length === 0 || rawLines.every(function(l) { return !l.trim(); })) return maxPx;

      var lo = minPx, hi = maxPx, best = minPx;

      for (var iter = 0; iter < 20; iter++) {
        var mid = Math.round((lo + hi) / 2);
        if (mid === lo) break;

        this.ctx.font = 'bold ' + mid + 'px "' + family + '", monospace';

        var totalWrapped = 0;
        var fits = true;
        for (var i = 0; i < rawLines.length; i++) {
          var line = rawLines[i];
          if (!line.trim()) { totalWrapped++; continue; }
          var wrapped = this._wrapText(line, areaW);
          totalWrapped += wrapped.length;
          // Check that each wrapped line actually fits the width
          for (var j = 0; j < wrapped.length; j++) {
            if (this.ctx.measureText(wrapped[j]).width > areaW) { fits = false; }
          }
        }

        var totalH = totalWrapped * mid * lineHeightRatio;
        if (totalH <= areaH && fits) { best = mid; lo = mid; }
        else { hi = mid; }
      }

      return best;
    }

    // ============================================================
    // CANVAS INTERACTION HELPER
    // ============================================================
    _getActivePhotoSide() {
      var activeSide = this.state.activeSide;
      var ss = this.sideState[activeSide];
      if (ss.mode === 'photo' && ss.photoUrl && ss._photoImg && ss._photoImg.complete) {
        return activeSide;
      }
      return null;
    }

    // ============================================================
    // DRAW PHOTO — customer's photo clipped to area
    // ============================================================
    _drawPhotoOnCanvas(sideKey, w, h) {
      var area = this._getArea(sideKey, 'photo');
      var ss = this.sideState[sideKey];

      if (!ss._photoImg || !ss._photoImg.complete) return;

      var areaX = w * area.x;
      var areaY = h * area.y;
      var areaW = w * area.width;
      var areaH = h * area.height;

      var transform = ss.photoTransform || { x: 0, y: 0, scale: 1, rotation: 0 };
      var scale = transform.scale;
      var rotation = transform.rotation || 0;

      // Calculate photo dimensions to cover the area
      var imgW = ss._photoImg.naturalWidth;
      var imgH = ss._photoImg.naturalHeight;
      var coverScale = Math.max(areaW / imgW, areaH / imgH) * scale;
      var drawW = imgW * coverScale;
      var drawH = imgH * coverScale;

      // Center + pan offset
      var centerX = areaX + areaW / 2 + transform.x * areaW;
      var centerY = areaY + areaH / 2 + transform.y * areaH;

      this.ctx.save();

      // Clip to area
      this.ctx.beginPath();
      this.ctx.rect(areaX, areaY, areaW, areaH);
      this.ctx.clip();

      // Rotate around photo center
      this.ctx.translate(centerX, centerY);
      this.ctx.rotate(rotation * Math.PI / 180);
      this.ctx.drawImage(ss._photoImg, -drawW / 2, -drawH / 2, drawW, drawH);

      this.ctx.restore();
    }

    // ============================================================
    // RENDER: COLORS (shared)
    // ============================================================
    _renderColors() {
      var container = this.el.querySelector('[data-pzd-colors]');
      if (!container) return;
      var self = this;

      container.innerHTML = '';
      // "Disponible" = habilitado en el admin Y con stock real en Shopify (si el sync está activo)
      var isAvail = function(c) {
        return c.available !== false && self._isVariantAvailable(c.name);
      };

      // Disponibles primero, agotados al final (sort estable: respeta el orden manual)
      var sortedColors = (this.config.colors || []).slice().sort(function(a, b) {
        return (isAvail(a) ? 0 : 1) - (isAvail(b) ? 0 : 1);
      });
      sortedColors.forEach(function(c) {
        var soldOut = !isAvail(c);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pzd__color-btn';
        if (self.config.colorSize) {
          btn.style.setProperty('--pzd-color-size', self.config.colorSize + 'px');
        }
        if (self.state.color === c.id) btn.classList.add('is-active');
        if (soldOut) btn.classList.add('is-unavailable');
        btn.dataset.colorId = c.id;
        btn.setAttribute('aria-label', c.name + (soldOut ? ' (Agotado)' : ''));

        var innerHtml;
        if (c.thumbnail) {
          innerHtml = '<img class="pzd__color-img" src="' + c.thumbnail + '" alt="' + c.name + '">';
        } else {
          innerHtml = '<div class="pzd__color-inner" style="background:' + c.hex + '"></div>';
        }

        btn.innerHTML =
          innerHtml +
          '<span class="pzd__color-name">' + c.name + (soldOut ? ' (Agotado)' : '') + '</span>';
        container.appendChild(btn);
      });
    }

    // ============================================================
    // RENDER: FONT PICKER (custom dropdown with font preview)
    // ============================================================
    _renderFontSelector() {
      var fonts = this.config.fonts || [];
      var self = this;

      // Build dropdown options and set initial preview for all pickers
      var pickers = this.el.querySelectorAll('[data-pzd-font-picker]');
      pickers.forEach(function(picker) {
        var dropdown = picker.querySelector('[data-pzd-font-dropdown]');
        var preview = picker.querySelector('[data-pzd-font-picker-preview]');
        if (!dropdown) return;

        dropdown.innerHTML = '';
        fonts.forEach(function(f) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'pzd__font-picker-option';
          if (f.family === self.state.font) btn.classList.add('is-active');
          btn.dataset.fontFamily = f.family;
          btn.textContent = f.label || f.family;
          btn.style.fontFamily = '"' + f.family + '", monospace';
          btn.style.textTransform = f.uppercase !== false ? 'uppercase' : 'none';
          btn.style.letterSpacing = (f.letterSpacing || 0) + 'em';
          btn.style.wordSpacing = (f.wordSpacing || 0) + 'em';
          dropdown.appendChild(btn);
        });

        // Set initial preview
        var current = fonts.find(function(f) { return f.family === self.state.font; });
        if (preview && current) {
          preview.textContent = current.label || current.family;
          preview.style.fontFamily = '"' + current.family + '", monospace';
          preview.style.textTransform = current.uppercase !== false ? 'uppercase' : 'none';
          preview.style.letterSpacing = (current.letterSpacing || 0) + 'em';
          preview.style.wordSpacing = (current.wordSpacing || 0) + 'em';
        }
      });
    }

    _updateFontPickerState() {
      var fonts = this.config.fonts || [];
      var self = this;
      var current = fonts.find(function(f) { return f.family === self.state.font; });

      var pickers = this.el.querySelectorAll('[data-pzd-font-picker]');
      pickers.forEach(function(picker) {
        // Update preview text with font properties
        var preview = picker.querySelector('[data-pzd-font-picker-preview]');
        if (preview && current) {
          preview.textContent = current.label || current.family;
          preview.style.fontFamily = '"' + current.family + '", monospace';
          preview.style.textTransform = current.uppercase !== false ? 'uppercase' : 'none';
          preview.style.letterSpacing = (current.letterSpacing || 0) + 'em';
          preview.style.wordSpacing = (current.wordSpacing || 0) + 'em';
        }

        // Update active option
        var options = picker.querySelectorAll('.pzd__font-picker-option');
        options.forEach(function(opt) {
          opt.classList.toggle('is-active', opt.dataset.fontFamily === self.state.font);
        });

        // Close dropdown
        picker.classList.remove('is-open');
      });
    }

    // ============================================================
    // TEXTAREA FONT — apply selected font to textareas
    // ============================================================
    _updateTextareaFonts() {
      this._updateTextareaFont('front');
      this._updateTextareaFont('back');
    }

    _updateTextareaFont(sideKey) {
      var textarea = this.el.querySelector('[data-pzd-textarea="' + sideKey + '"]');
      if (!textarea) return;
      var fontCfg = this._getFontConfig();
      var family = this.state.font || 'monospace';
      textarea.style.fontFamily = '"' + family + '", monospace';
      textarea.style.textTransform = fontCfg.uppercase !== false ? 'uppercase' : 'none';
      textarea.style.letterSpacing = (fontCfg.letterSpacing || 0) + 'em';
      textarea.style.wordSpacing = (fontCfg.wordSpacing || 0) + 'em';
      textarea.style.lineHeight = String(fontCfg.lineHeight || 1.5);
    }

    // ============================================================
    // GET CURRENT FONT CONFIG — returns the full font object
    // ============================================================
    _getFontConfig() {
      var fonts = this.config.fonts || [];
      var self = this;
      return fonts.find(function(f) { return f.family === self.state.font; }) || {};
    }

    // ============================================================
    // CHAR COUNT — based on selected font's maxChars
    // ============================================================
    _getMaxChars() {
      var f = this._getFontConfig();
      return f.maxChars || 120;
    }

    // Remueve emojis (emoticons, símbolos, banderas, variation selectors, ZWJ).
    // Preserva ♥ (U+2665) porque es un carácter grabable, no un emoji a color.
    _stripEmojis(str) {
      if (!str) return str;
      return str.replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{2664}\u{2666}-\u{27BF}\u{2300}-\u{23FF}\u{FE00}-\u{FE0F}\u{1F1E6}-\u{1F1FF}\u{200D}\u{20E3}\u{2B00}-\u{2BFF}]/gu, '');
    }

    _updateCharCount(sideKey) {
      var counter = this.el.querySelector('[data-pzd-char-count="' + sideKey + '"]');
      if (!counter) return;

      var maxChars = this._getMaxChars();
      var ss = this.sideState[sideKey];
      var remaining = maxChars - ss.text.length;

      counter.textContent = remaining + ' / ' + maxChars;
      counter.classList.remove('is-warning', 'is-full');
      if (remaining <= 0) counter.classList.add('is-full');
      else if (remaining <= 10) counter.classList.add('is-warning');
    }

    // ============================================================
    // PHOTO UPLOAD + EDITOR
    // ============================================================
    _handlePhotoUpload(sideKey, file) {
      if (!file || !file.type.startsWith('image/')) {
        this._showMsg('error', 'Solo se permiten archivos de imagen.');
        return;
      }

      // Validate format (JPEG, PNG, WebP only)
      var allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (allowedTypes.indexOf(file.type) === -1) {
        this._showMsg('error', 'Formato no soportado. Usa JPEG, PNG o WebP.');
        return;
      }

      var ss = this.sideState[sideKey];
      ss.photoFile = file;

      // Create object URL for display
      if (ss.photoUrl) URL.revokeObjectURL(ss.photoUrl);
      ss.photoUrl = URL.createObjectURL(file);

      // Reset transform
      ss.photoTransform = { x: 0, y: 0, scale: 1, rotation: 0 };

      // Load image for canvas
      var self = this;
      var img = new Image();
      img.onload = function() {
        ss._photoImg = img;
        self._drawCanvas();
        self._updateCanvasPhotoControls();
      };
      img.src = ss.photoUrl;

      // Hide upload area (photo now lives in the canvas preview)
      var upload = this.el.querySelector('[data-pzd-photo-upload="' + sideKey + '"]');
      if (upload) upload.style.display = 'none';

      // Update canvas controls (zoom slider, change button, hint)
      this._updateCanvasPhotoControls();

      // Upload to Firebase Storage in background
      this._uploadCustomerPhoto(sideKey);

      this._updateStatus();
      this._injectHiddenInputs();
    }

    // ============================================================
    // V2: PHOTO CONTROLS — D-pad + zoom per side (inside accordion)
    // ============================================================
    _updateCanvasPhotoControls() {
      // V2: update per-side photo controls (show/hide + sync zoom)
      this._updatePhotoControls('front');
      this._updatePhotoControls('back');
    }

    _updatePhotoControls(sideKey) {
      var controlsEl = this.el.querySelector('[data-pzd-photo-controls="' + sideKey + '"]');
      if (!controlsEl) return;

      var ss = this.sideState[sideKey];
      var hasPhoto = ss.mode === 'photo' && ss.photoUrl && ss._photoImg && ss._photoImg.complete;

      // Show/hide context hint
      var hintEl = this.el.querySelector('[data-pzd-context-hint="' + sideKey + '"]');

      if (hasPhoto) {
        controlsEl.style.display = '';
        if (hintEl) hintEl.style.display = '';
        // Sync position sliders (Y is inverted: negative transform = positive slider)
        var posSliders = controlsEl.querySelectorAll('[data-pzd-photo-pos]');
        posSliders.forEach(function(slider) {
          var axis = slider.dataset.pzdAxis;
          if (!axis) return;
          var val = ss.photoTransform[axis] * 100;
          slider.value = Math.round(axis === 'y' ? -val : val);
        });
        // Sync zoom slider
        var zoomSlider = controlsEl.querySelector('[data-pzd-photo-zoom]');
        if (zoomSlider) {
          zoomSlider.value = Math.round(ss.photoTransform.scale * 150);
        }
        // Sync rotation slider
        var rotSlider = controlsEl.querySelector('[data-pzd-photo-rotate]');
        if (rotSlider) {
          rotSlider.value = Math.round(ss.photoTransform.rotation || 0);
        }
        // Hide the upload area since photo is loaded
        var uploadEl = this.el.querySelector('[data-pzd-photo-upload="' + sideKey + '"]');
        if (uploadEl) uploadEl.style.display = 'none';
      } else {
        controlsEl.style.display = 'none';
        if (hintEl) hintEl.style.display = 'none';
        // Show upload area if in photo mode
        if (ss.mode === 'photo') {
          var uploadEl = this.el.querySelector('[data-pzd-photo-upload="' + sideKey + '"]');
          if (uploadEl) uploadEl.style.display = '';
        }
      }

      // Update status
      var statusEl = this.el.querySelector('[data-pzd-photo-status="' + sideKey + '"]');
      if (statusEl) {
        if (ss.uploading) {
          statusEl.textContent = 'Subiendo...';
          statusEl.className = 'pzd__photo-status is-visible is-uploading';
        } else {
          statusEl.textContent = '';
          statusEl.className = 'pzd__photo-status';
        }
      }
    }

    // ============================================================
    // V2: MANUAL PHOTO CONTROLS — D-pad + zoom (replaces canvas drag)
    // ============================================================
    _setupPhotoControls() {
      var self = this;

      // Guard: prevent slider thumb from jumping when tapping the track.
      // Each slider gets its own touchstart listener that checks if the touch
      // hit the thumb. If not, it locks the slider value until the touch ends.
      var _sliderLocked = new WeakMap();

      this.el.querySelectorAll('.pzd__photo-pos-slider').forEach(function(slider) {
        slider.addEventListener('touchstart', function(e) {
          var rect = slider.getBoundingClientRect();
          var min = parseFloat(slider.min);
          var max = parseFloat(slider.max);
          var val = parseFloat(slider.value);
          var ratio = (val - min) / (max - min);
          var thumbX = rect.left + ratio * rect.width;
          var clientX = e.touches[0].clientX;

          if (Math.abs(clientX - thumbX) > 18) {
            // Touch was on the track — lock slider to current value
            _sliderLocked.set(slider, val);
            function unlock() {
              _sliderLocked.delete(slider);
              // Restore value one final time
              slider.value = val;
              document.removeEventListener('touchend', unlock);
              document.removeEventListener('touchcancel', unlock);
            }
            document.addEventListener('touchend', unlock);
            document.addEventListener('touchcancel', unlock);
          }
        }, { passive: true });

        slider.addEventListener('input', function() {
          if (_sliderLocked.has(slider)) {
            slider.value = _sliderLocked.get(slider);
          }
        });
      });

      // Position sliders (X horizontal, Y vertical) + Zoom slider
      this.el.addEventListener('input', function(e) {
        // Position slider
        var posSlider = e.target.closest('[data-pzd-photo-pos]');
        if (posSlider) {
          var sideKey = posSlider.dataset.pzdPhotoPos;
          var axis = posSlider.dataset.pzdAxis;
          if (!sideKey || !axis) return;
          var ss = self.sideState[sideKey];
          if (!ss || !ss.photoUrl) return;
          var raw = parseInt(posSlider.value, 10) / 100;
          // Y axis is inverted: slider right = photo moves up (negative Y)
          ss.photoTransform[axis] = axis === 'y' ? -raw : raw;
          self._drawCanvas();
          return;
        }
        // Zoom slider
        var zoomSlider = e.target.closest('[data-pzd-photo-zoom]');
        if (zoomSlider) {
          var sideKey = zoomSlider.dataset.pzdPhotoZoom;
          if (!sideKey) return;
          var ss = self.sideState[sideKey];
          if (!ss) return;
          // Zoom slider: 150 = 1x (center), 50 = 0.33x, 250 = 1.67x
          ss.photoTransform.scale = parseInt(zoomSlider.value, 10) / 150;
          self._drawCanvas();
          return;
        }
        // Rotation slider
        var rotSlider = e.target.closest('[data-pzd-photo-rotate]');
        if (rotSlider) {
          var sideKey = rotSlider.dataset.pzdPhotoRotate;
          if (!sideKey) return;
          var ss = self.sideState[sideKey];
          if (!ss) return;
          ss.photoTransform.rotation = parseInt(rotSlider.value, 10);
          self._drawCanvas();
          return;
        }
      });

      // Schedule upload on slider change end (position, zoom, or rotation)
      this.el.addEventListener('change', function(e) {
        if (e.target.closest('[data-pzd-photo-pos]') || e.target.closest('[data-pzd-photo-zoom]') || e.target.closest('[data-pzd-photo-rotate]')) {
          self._schedulePreviewUpload();
        }
      });

      // Per-side change photo buttons — resets photo and shows upload area
      this.el.addEventListener('click', function(e) {
        var changeBtn = e.target.closest('[data-pzd-photo-change]');
        if (!changeBtn) return;
        var sideKey = changeBtn.dataset.pzdPhotoChange;
        if (!sideKey) return;

        // Show upload area again
        var upload = self.el.querySelector('[data-pzd-photo-upload="' + sideKey + '"]');
        if (upload) upload.style.display = '';

        // Reset file input
        var input = self.el.querySelector('[data-pzd-photo-input="' + sideKey + '"]');
        if (input) input.value = '';

        // Clear photo state
        var ss = self.sideState[sideKey];
        if (ss.photoUrl) URL.revokeObjectURL(ss.photoUrl);
        ss.photoFile = null;
        ss.photoUrl = null;
        ss.photoUploadUrl = null;
        ss._photoImg = null;
        ss.photoTransform = { x: 0, y: 0, scale: 1, rotation: 0 };

        // Reset position sliders to center
        var controlsEl = self.el.querySelector('[data-pzd-photo-controls="' + sideKey + '"]');
        if (controlsEl) {
          controlsEl.querySelectorAll('[data-pzd-photo-pos]').forEach(function(s) { s.value = 0; });
          var zs = controlsEl.querySelector('[data-pzd-photo-zoom]');
          if (zs) zs.value = 150;
          var rs = controlsEl.querySelector('[data-pzd-photo-rotate]');
          if (rs) rs.value = 0;
        }

        self._drawCanvas();
        self._updateCanvasPhotoControls();
        self._injectHiddenInputs();
        self._updateStatus();
      });
    }

    // V2: Canvas interaction disabled — no drag/pinch
    _initCanvasInteraction() {
      // Intentionally empty — v2 uses position sliders instead
      return;
      /* --- Original canvas drag/pinch code below (kept for reference) ---
      if (!this.canvas) return;
      var self = this;
      var canvas = this.canvas;

      // --- TOUCH START ---
      // Don't preventDefault here — we need to detect scroll vs drag intent first
      canvas.addEventListener('touchstart', function(e) {
        var side = self._getActivePhotoSide();
        if (!side) return; // No photo — let scroll happen normally

        if (e.touches.length === 1) {
          var ss = self.sideState[side];
          // Store touch start data but don't commit to dragging yet
          self._canvasDrag.pending = true;
          self._canvasDrag.decided = false;
          self._canvasDrag.targetSide = side;
          self._canvasDrag.startX = e.touches[0].clientX;
          self._canvasDrag.startY = e.touches[0].clientY;
          self._canvasDrag.startTx = ss.photoTransform.x;
          self._canvasDrag.startTy = ss.photoTransform.y;
        } else if (e.touches.length === 2) {
          e.preventDefault();
          self._canvasDrag.pending = false;
          self._canvasDrag.active = false;
          self._canvasPinch.active = true;
          self._canvasPinch.targetSide = side;
          self._canvasPinch.lastDist = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
        }
      }, { passive: false });

      // --- TOUCH MOVE ---
      canvas.addEventListener('touchmove', function(e) {
        // Pinch zoom — always prevent scroll
        if (self._canvasPinch.active && e.touches.length === 2) {
          e.preventDefault();
          var dist = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
          if (self._canvasPinch.lastDist > 0) {
            var side = self._canvasPinch.targetSide;
            var ss = self.sideState[side];
            var ratio = dist / self._canvasPinch.lastDist;
            ss.photoTransform.scale = Math.max(1, Math.min(3, ss.photoTransform.scale * ratio));
            self._drawCanvas();
            var zoomSlider = self.el.querySelector('[data-pzd-canvas-zoom]');
            if (zoomSlider) zoomSlider.value = Math.round(ss.photoTransform.scale * 100);
          }
          self._canvasPinch.lastDist = dist;
          return;
        }

        // Single finger — decide if drag or scroll
        if (self._canvasDrag.pending && e.touches.length === 1) {
          var moveX = Math.abs(e.touches[0].clientX - self._canvasDrag.startX);
          var moveY = Math.abs(e.touches[0].clientY - self._canvasDrag.startY);

          // If haven't decided yet, wait for enough movement (8px threshold)
          if (!self._canvasDrag.decided) {
            if (moveX < 8 && moveY < 8) return; // Not enough movement yet

            if (moveX > moveY) {
              // Horizontal movement dominant — user wants to drag the photo
              self._canvasDrag.decided = true;
              self._canvasDrag.active = true;

              // Auto-hide hint after first interaction
              if (!self._canvasInteracted) {
                self._canvasInteracted = true;
                var hintEl = self.el.querySelector('[data-pzd-canvas-hint]');
                if (hintEl) hintEl.style.opacity = '0';
              }
            } else {
              // Vertical movement dominant — user wants to scroll
              self._canvasDrag.decided = true;
              self._canvasDrag.active = false;
              self._canvasDrag.pending = false;
              return; // Let scroll happen naturally
            }
          }
        }

        // Active drag — move the photo
        if (self._canvasDrag.active && e.touches.length === 1) {
          e.preventDefault();
          var side = self._canvasDrag.targetSide;
          var ss = self.sideState[side];
          var rect = canvas.getBoundingClientRect();

          var sidesConfig = self.config.sides || {};
          var sideCfg = sidesConfig[side] || {};
          var photoCfg = sideCfg.photo || {};
          var area = photoCfg.area || { x: 0.05, y: 0.05, width: 0.90, height: 0.90 };

          var areaScreenW = rect.width * area.width;
          var areaScreenH = rect.height * area.height;

          var dx = (e.touches[0].clientX - self._canvasDrag.startX) / areaScreenW;
          var dy = (e.touches[0].clientY - self._canvasDrag.startY) / areaScreenH;

          ss.photoTransform.x = self._canvasDrag.startTx + dx;
          ss.photoTransform.y = self._canvasDrag.startTy + dy;
          self._drawCanvas();
        }
      }, { passive: false });

      // --- TOUCH END ---
      canvas.addEventListener('touchend', function(e) {
        var wasDragging = self._canvasDrag.active || self._canvasPinch.active;
        self._canvasDrag.active = false;
        self._canvasDrag.pending = false;
        self._canvasDrag.decided = false;
        self._canvasPinch.active = false;
        self._canvasPinch.lastDist = 0;
        if (wasDragging) self._schedulePreviewUpload();
      });

      // --- MOUSE DOWN ---
      canvas.addEventListener('mousedown', function(e) {
        var side = self._getActivePhotoSide();
        if (!side) return;
        e.preventDefault();
        var ss = self.sideState[side];
        self._canvasDrag.active = true;
        self._canvasDrag.targetSide = side;
        self._canvasDrag.startX = e.clientX;
        self._canvasDrag.startY = e.clientY;
        self._canvasDrag.startTx = ss.photoTransform.x;
        self._canvasDrag.startTy = ss.photoTransform.y;
        canvas.style.cursor = 'grabbing';

        // Auto-hide hint
        if (!self._canvasInteracted) {
          self._canvasInteracted = true;
          var hintEl = self.el.querySelector('[data-pzd-canvas-hint]');
          if (hintEl) hintEl.style.opacity = '0';
        }
      });

      // --- MOUSE MOVE ---
      document.addEventListener('mousemove', function(e) {
        if (!self._canvasDrag.active) return;
        var side = self._canvasDrag.targetSide;
        var ss = self.sideState[side];
        var rect = canvas.getBoundingClientRect();

        var sidesConfig = self.config.sides || {};
        var sideCfg = sidesConfig[side] || {};
        var photoCfg = sideCfg.photo || {};
        var area = photoCfg.area || { x: 0.05, y: 0.05, width: 0.90, height: 0.90 };

        var areaScreenW = rect.width * area.width;
        var areaScreenH = rect.height * area.height;

        var dx = (e.clientX - self._canvasDrag.startX) / areaScreenW;
        var dy = (e.clientY - self._canvasDrag.startY) / areaScreenH;

        ss.photoTransform.x = self._canvasDrag.startTx + dx;
        ss.photoTransform.y = self._canvasDrag.startTy + dy;
        self._drawCanvas();
      });

      // --- MOUSE UP ---
      document.addEventListener('mouseup', function(e) {
        if (self._canvasDrag.active) {
          self._canvasDrag.active = false;
          canvas.style.cursor = '';
          self._schedulePreviewUpload();
        }
      });
    --- End of original canvas interaction code --- */
    }

    async _uploadCustomerPhoto(sideKey) {
      var ss = this.sideState[sideKey];
      if (!ss.photoFile) return;

      var uploadUrl = this.el.dataset.pzdUploadUrl;
      var uploadKey = this.el.dataset.pzdUploadKey;
      if (!uploadUrl || !uploadKey) return;

      ss.uploading = true;
      this._setPhotoStatus(sideKey, 'uploading', 'Subiendo imagen...');
      this._updateStatus();

      try {
        // Resize if needed (max 2048px) then convert to data URL for upload
        var dataUrl = await this._resizeForUpload(ss.photoFile);

        var controller = new AbortController();
        var timeoutId = setTimeout(function() { controller.abort(); }, 30000);

        var response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Upload-Key': uploadKey
          },
          body: JSON.stringify({
            image: dataUrl,
            productId: this.el.dataset.pzdVariantId || '',
            variantId: (this.state.variantId || '') + '-photo-' + sideKey
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          var result = await response.json();
          if (result.url) {
            ss.photoUploadUrl = result.url;
            this._setPhotoStatus(sideKey, 'done', 'Imagen subida');
          }
        } else {
          // Try to get error message from server
          var errMsg = 'Error al subir. Intenta de nuevo.';
          try {
            var errResult = await response.json();
            if (errResult.error) errMsg = errResult.error;
          } catch(e) {}
          this._setPhotoStatus(sideKey, 'error', errMsg);
        }
      } catch (err) {
        console.warn('[PZD] Photo upload error:', sideKey, err);
        this._setPhotoStatus(sideKey, 'error', 'Error al subir. Intenta de nuevo.');
      }

      ss.uploading = false;
      this._updateStatus();
      this._injectHiddenInputs();
    }

    _fileToDataUrl(file) {
      return new Promise(function(resolve, reject) {
        var reader = new FileReader();
        reader.onload = function() { resolve(reader.result); };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    // Resize image if too large for upload. Returns a JPEG data URL.
    // If image is already small enough, returns original file as data URL.
    _resizeForUpload(file) {
      var maxDim = 2048;
      var self = this;

      return new Promise(function(resolve, reject) {
        var url = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function() {
          var w = img.naturalWidth;
          var h = img.naturalHeight;

          // If small enough, just read original file (preserves format)
          if (w <= maxDim && h <= maxDim && file.size <= 4 * 1024 * 1024) {
            URL.revokeObjectURL(url);
            self._fileToDataUrl(file).then(resolve).catch(reject);
            return;
          }

          // Resize: reduce to maxDim and export as JPEG
          if (w > maxDim || h > maxDim) {
            var ratio = Math.min(maxDim / w, maxDim / h);
            w = Math.round(w * ratio);
            h = Math.round(h * ratio);
          }
          var cvs = document.createElement('canvas');
          cvs.width = w;
          cvs.height = h;
          var ctx = cvs.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          URL.revokeObjectURL(url);
          resolve(__pzSobreBlanco(cvs).toDataURL('image/jpeg', 0.85));
        };
        img.onerror = function() {
          URL.revokeObjectURL(url);
          reject(new Error('No se pudo procesar la imagen'));
        };
        img.src = url;
      });
    }

    _setPhotoStatus(sideKey, type, text) {
      // V2: Update per-side photo status (inside accordion panel)
      var statusEl = this.el.querySelector('[data-pzd-photo-status="' + sideKey + '"]');
      if (!statusEl) return;
      statusEl.className = 'pzd__photo-status is-visible is-' + type;
      statusEl.textContent = text;

      // Auto-hide success after 3s
      if (type === 'done') {
        setTimeout(function() {
          statusEl.classList.remove('is-visible');
        }, 3000);
      }
    }

    // ============================================================
    // PHOTO DRAG/PAN
    // ============================================================
    // _initPhotoDrag removed — replaced by _initCanvasInteraction

    // ============================================================
    // PREVIEW MODAL
    // ============================================================
    _openPreviewModal() {
      // Primera vez: mover el modal al body para escapar de ancestros con
      // transform/filter/perspective que rompen position:fixed (común en temas Shopify).
      if (!this.previewModal) {
        this.previewModal = this.el.querySelector('[data-pzd-preview-modal]');
        if (!this.previewModal) return;
        // Copiar las variables CSS (--pzd-*) del root al modal ANTES de moverlo.
        // Sin esto el modal pierde las variables heredadas (accent color, etc.).
        var rootStyle = this.el.style;
        for (var i = 0; i < rootStyle.length; i++) {
          var prop = rootStyle[i];
          if (prop && prop.indexOf('--') === 0) {
            this.previewModal.style.setProperty(prop, rootStyle.getPropertyValue(prop));
          }
        }
        document.body.appendChild(this.previewModal);
      }
      var modal = this.previewModal;

      var srcCanvas = this.el.querySelector('[data-pzd-canvas]');
      var modalCanvas = modal.querySelector('[data-pzd-preview-modal-canvas]');
      if (srcCanvas && modalCanvas) {
        modalCanvas.width = srcCanvas.width;
        modalCanvas.height = srcCanvas.height;
        var ctx = modalCanvas.getContext('2d');
        ctx.drawImage(srcCanvas, 0, 0);
      }

      modal.style.display = '';
      document.body.style.overflow = 'hidden';
    }

    _closePreviewModal() {
      var modal = this.previewModal || this.el.querySelector('[data-pzd-preview-modal]');
      if (modal) modal.style.display = 'none';
      document.body.style.overflow = '';
    }

    // ============================================================
    // EVENTS
    // ============================================================
    _bindEvents() {
      var self = this;

      // Color click
      this.el.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-color-id]');
        if (btn && !btn.classList.contains('is-unavailable')) {
          self._selectColor(btn.dataset.colorId);
        }
      });

      // Mode toggle click
      this.el.addEventListener('click', function(e) {
        var modeBtn = e.target.closest('[data-pzd-mode]');
        if (!modeBtn) return;
        var toggle = modeBtn.closest('[data-pzd-mode-toggle]');
        if (!toggle) return;
        var sideKey = toggle.dataset.pzdModeToggle;
        var mode = modeBtn.dataset.pzdMode;
        self._applyMode(sideKey, mode);
        self._loadBackgroundImage(); // Image changes based on mode combination
        self._injectHiddenInputs();
        self._updateStatus();
      });

      // Textarea input
      this.el.addEventListener('input', function(e) {
        if (e.target.hasAttribute('data-pzd-textarea')) {
          var sideKey = e.target.dataset.pzdTextarea;
          if (self.config.allowEmojis === false) {
            var cleaned = self._stripEmojis(e.target.value);
            if (cleaned !== e.target.value) e.target.value = cleaned;
          }
          var maxChars = self._getMaxChars();
          var val = e.target.value;

          // Enforce max chars
          if (val.length > maxChars) {
            e.target.value = val.substring(0, maxChars);
          }

          self.sideState[sideKey].text = e.target.value;
          e.target.classList.remove('is-error');
          self._updateCharCount(sideKey);
          self._drawCanvas();
          self._injectHiddenInputs();
          self._updateStatus();
        }
      });

      // Font picker — toggle dropdown
      this.el.addEventListener('click', function(e) {
        var pickerBtn = e.target.closest('[data-pzd-font-picker-btn]');
        if (pickerBtn) {
          var picker = pickerBtn.closest('[data-pzd-font-picker]');
          if (picker) {
            // Close all other pickers first
            self.el.querySelectorAll('[data-pzd-font-picker]').forEach(function(p) {
              if (p !== picker) p.classList.remove('is-open');
            });
            picker.classList.toggle('is-open');
          }
          return;
        }

        // Font picker — select option
        var option = e.target.closest('.pzd__font-picker-option');
        if (option) {
          var family = option.dataset.fontFamily;
          if (family && family !== self.state.font) {
            self.state.font = family;
            self._updateFontPickerState();
            self._updateTextareaFonts();
            self._updateCharCount('front');
            self._updateCharCount('back');

            // Enforce max chars on both textareas
            var maxChars = self._getMaxChars();
            ['front', 'back'].forEach(function(sk) {
              var ta = self.el.querySelector('[data-pzd-textarea="' + sk + '"]');
              if (ta && ta.value.length > maxChars) {
                ta.value = ta.value.substring(0, maxChars);
                self.sideState[sk].text = ta.value;
              }
            });

            self._drawCanvas();
            self._injectHiddenInputs();
          } else {
            // Same font — just close
            var picker = option.closest('[data-pzd-font-picker]');
            if (picker) picker.classList.remove('is-open');
          }
          return;
        }

        // Close picker if clicking outside
        if (!e.target.closest('[data-pzd-font-picker]')) {
          self.el.querySelectorAll('[data-pzd-font-picker]').forEach(function(p) {
            p.classList.remove('is-open');
          });
        }
      });

      // Photo upload — click
      this.el.addEventListener('click', function(e) {
        var uploadArea = e.target.closest('[data-pzd-photo-upload]');
        if (!uploadArea) return;
        var sideKey = uploadArea.dataset.pzdPhotoUpload;
        var input = self.el.querySelector('[data-pzd-photo-input="' + sideKey + '"]');
        if (input) input.click();
      });

      // Photo upload — file selected
      this.el.addEventListener('change', function(e) {
        if (e.target.hasAttribute('data-pzd-photo-input')) {
          var sideKey = e.target.dataset.pzdPhotoInput;
          if (e.target.files && e.target.files[0]) {
            self._handlePhotoUpload(sideKey, e.target.files[0]);
          }
        }
      });

      // Photo upload — drag & drop
      ['front', 'back'].forEach(function(sideKey) {
        var uploadArea = self.el.querySelector('[data-pzd-photo-upload="' + sideKey + '"]');
        if (!uploadArea) return;

        uploadArea.addEventListener('dragover', function(e) {
          e.preventDefault();
          uploadArea.classList.add('is-dragover');
        });

        uploadArea.addEventListener('dragleave', function(e) {
          uploadArea.classList.remove('is-dragover');
        });

        uploadArea.addEventListener('drop', function(e) {
          e.preventDefault();
          uploadArea.classList.remove('is-dragover');
          if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            self._handlePhotoUpload(sideKey, e.dataTransfer.files[0]);
          }
        });
      });

      // V2: Old canvas-photo-change and canvas-zoom handlers removed
      // Photo controls (D-pad, zoom, change) now handled by _setupPhotoControls()

      // Close font picker on outside click (document level)
      document.addEventListener('click', function(e) {
        if (!e.target.closest('[data-pzd-font-picker]')) {
          self.el.querySelectorAll('[data-pzd-font-picker]').forEach(function(p) {
            p.classList.remove('is-open');
          });
        }
      });

      // ---- Preview button → open modal ----
      this.el.addEventListener('click', function(e) {
        if (e.target.closest('[data-pzd-open-preview]')) {
          self._openPreviewModal();
        }
      });

      // ---- Close preview modal ----
      // Listener en document porque el modal vive en <body> (no en this.el)
      // una vez que se abre por primera vez.
      document.addEventListener('click', function(e) {
        if (e.target.closest('[data-pzd-preview-modal-close]')) {
          // Solo cerrar si el modal cerrado es el de ESTA instancia
          var modal = e.target.closest('[data-pzd-preview-modal]');
          if (modal && modal === self.previewModal) {
            self._closePreviewModal();
          }
        }
      });

      // V2: Manual photo controls (D-pad, zoom, change photo)
      this._setupPhotoControls();
    }

    // ============================================================
    // SELECT COLOR
    // ============================================================
    _selectColor(id) {
      // No permitir elegir un color agotado
      var target = (this.config.colors || []).find(function(c) { return c.id === id; });
      if (target && (target.available === false || !this._isVariantAvailable(target.name))) return;

      this.state.color = id;
      this.el.querySelectorAll('[data-color-id]').forEach(function(b) {
        b.classList.toggle('is-active', b.dataset.colorId === id);
      });
      this._updateBadge();
      this._loadBackgroundImage();
      this._updateVariantId();
      this._injectHiddenInputs();
    }

    _updateBadge() {
      var badge = this.el.querySelector('[data-pzd-color-name]');
      if (!badge) return;
      var self = this;
      var c = (this.config.colors || []).find(function(c) { return c.id === self.state.color; });
      badge.textContent = c ? c.name : '';
    }

    // ============================================================
    // PREVIEW UPLOAD — background debounce (single canvas)
    // ============================================================
    _schedulePreviewUpload() {
      var self = this;
      if (this._uploadTimer) clearTimeout(this._uploadTimer);
      this._uploadTimer = setTimeout(function() {
        self._doUploadPreview();
      }, 1500);
    }

    async _doUploadPreview() {
      if (!this.canvas || !this._bgImg) return;

      var uploadUrl = this.el.dataset.pzdUploadUrl;
      var uploadKey = this.el.dataset.pzdUploadKey;
      if (!uploadUrl || !uploadKey) return;

      try {
        var dataUrl = __pzSobreBlanco(this.canvas).toDataURL('image/jpeg', 0.8);

        var controller = new AbortController();
        var timeoutId = setTimeout(function() { controller.abort(); }, 15000);

        var response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Upload-Key': uploadKey
          },
          body: JSON.stringify({
            image: dataUrl,
            productId: this.el.dataset.pzdVariantId || '',
            variantId: (this.state.variantId || '') + '-dual-preview'
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          var result = await response.json();
          if (result.url) {
            this._previewUrl = result.url;
            this._injectHiddenInputs();
          }
        }
      } catch (err) {
        console.warn('[PZD] Preview upload error:', err);
      }
    }

    // ============================================================
    // STATUS INDICATOR
    // ============================================================
    _updateStatus() {
      var statusEl = this.el.querySelector('[data-pzd-status]');
      if (!statusEl) return;

      var sidesConfig = this.config.sides || {};
      var sideKeys = ['front', 'back'];
      var issues = [];
      var allGood = true;

      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        var ss = this.sideState[key];
        var sideCfg = sidesConfig[key] || {};
        var sideLabel = sideCfg.label || (key === 'front' ? 'Adelante' : 'Atrás');

        if (ss.mode === 'text' && !ss.text.trim()) {
          issues.push(sideLabel + ': escribe tu texto');
          allGood = false;
        } else if (ss.mode === 'photo' && !ss.photoUrl) {
          issues.push(sideLabel + ': sube tu foto');
          allGood = false;
        } else if (ss.mode === 'photo' && ss.uploading) {
          issues.push(sideLabel + ': subiendo foto...');
          allGood = false;
        }

        // Update tab icon
        var icon = this.el.querySelector('[data-pzd-tab-icon="' + key + '"]');
        if (icon) {
          icon.classList.remove('is-ready', 'is-pending');
          var sideReady = (ss.mode === 'text' && ss.text.trim()) ||
                          (ss.mode === 'photo' && ss.photoUrl && !ss.uploading);
          icon.classList.add(sideReady ? 'is-ready' : 'is-pending');
        }
      }

      statusEl.classList.add('is-visible');
      statusEl.classList.remove('is-pending', 'is-ready');

      if (allGood) {
        statusEl.classList.add('is-ready');
        statusEl.textContent = '\u2713\u2002Listo \u2014 puedes hacer tu pedido';
      } else {
        statusEl.classList.add('is-pending');
        statusEl.textContent = 'Pendiente \u2014 ' + issues.join('; ');
      }
    }

    // ============================================================
    // VALIDATION
    // ============================================================
    _validate() {
      this._hideMsg();
      var valid = true;
      var issues = [];
      var firstFailSide = null;
      var sidesConfig = this.config.sides || {};

      // Stock real de Shopify (solo si el sync está activo)
      if (this.config.enableStockSync === true) {
        // Producto simple agotado (stock global) → bloquear todo con un mensaje claro.
        if (this._isSimpleProduct() && !this.productAvailable) {
          this._showMsg('error', 'Este producto está agotado.');
          return false;
        }
        var stColor = this._getActiveColor();
        if (stColor && !this._isVariantAvailable(stColor.name)) {
          this._showMsg('error', 'Este color está agotado. Elegí otro.');
          return false;
        }
      }

      var sideKeys = ['front', 'back'];
      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        var ss = this.sideState[key];
        var sideCfg = sidesConfig[key] || {};
        var sideLabel = sideCfg.label || (key === 'front' ? 'Adelante' : 'Atrás');

        if (ss.mode === 'text') {
          if (!ss.text.trim()) {
            valid = false;
            issues.push(sideLabel + ': escribe tu texto');
            var textarea = this.el.querySelector('[data-pzd-textarea="' + key + '"]');
            if (textarea) textarea.classList.add('is-error');
            if (!firstFailSide) firstFailSide = key;
          }
        } else if (ss.mode === 'photo') {
          if (!ss.photoUrl) {
            valid = false;
            issues.push(sideLabel + ': sube tu foto');
            if (!firstFailSide) firstFailSide = key;
          } else if (ss.uploading) {
            valid = false;
            issues.push(sideLabel + ': la foto se está subiendo, espera un momento');
            if (!firstFailSide) firstFailSide = key;
          } else if (!ss.photoUploadUrl) {
            valid = false;
            issues.push(sideLabel + ': error al subir foto, intenta de nuevo');
            if (!firstFailSide) firstFailSide = key;
          }
        }
      }

      if (!valid) {
        if (firstFailSide) this._switchSide(firstFailSide);
        this._showMsg('error', 'Completa: ' + issues.join('; '));
      }

      return valid;
    }

    // ============================================================
    // UI HELPERS
    // ============================================================
    _showMsg(type, text) {
      this.el.querySelectorAll('[data-pzd-msg]').forEach(function(m) { m.classList.remove('is-visible'); });
      var el = this.el.querySelector('[data-pzd-msg="' + type + '"]');
      if (el) { el.textContent = text; el.classList.add('is-visible'); }
    }

    _hideMsg() {
      this.el.querySelectorAll('[data-pzd-msg]').forEach(function(m) { m.classList.remove('is-visible'); });
    }
  }

  // ============================================================
  // AUTO-INIT
  // ============================================================
  function initAll() {
    document.querySelectorAll('[data-product-personalizer-dual]').forEach(function(el) {
      if (!el._pzd) el._pzd = new ProductPersonalizerDual(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function(e) {
    var el = e.target.querySelector('[data-product-personalizer-dual]');
    if (el) el._pzd = new ProductPersonalizerDual(el);
  });

})();
